import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container, Row, Col, Form, InputGroup,
  Pagination, Image, Spinner, Modal, Badge,
} from "react-bootstrap";
import NavbarFacach from "../components/NavbarFacach";
import FooterFacach from "../components/FooterFacach";
import watermark from "../assets/fondo.png";
import "../components/watermarks.css";
import "../components/memorias.css";
import logoFacach from "../assets/isotipo.png";
import { useSiteSettings } from '../hooks/useSiteSettings';
import fallbackBanner from '../assets/imagen.jpg';
import * as pdfjsLib from 'pdfjs-dist';
import "../components/memorias.css";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const API_URL = "https://facach.coop.hn/api";

const pageWindow = (current, total, radius = 1) => {
  const start = Math.max(1, current - radius);
  const end = Math.min(total, current + radius);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

// ── Carátula PDF ──────────────────────────────────────────────────────────────
function PdfCover({ url, title }) {
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!url) { setError(true); setLoading(false); return; }
    let cancelled = false;
    const render = async () => {
      try {
        const pdf = await pdfjsLib.getDocument({ url, withCredentials: false }).promise;
        const page = await pdf.getPage(1);
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const vp = page.getViewport({ scale: 1.2 });
        canvas.width = vp.width;
        canvas.height = vp.height;
        await page.render({ canvasContext: ctx, viewport: vp }).promise;
        if (!cancelled) setLoading(false);
      } catch {
        if (!cancelled) { setError(true); setLoading(false); }
      }
    };
    render();
    return () => { cancelled = true; };
  }, [url]);

  if (error) {
    return (
      <div className="doc-cover-placeholder">
        <img src={logoFacach} alt={title} className="doc-placeholder-logo" />
        <div className="doc-placeholder-title">{title}</div>
      </div>
    );
  }

  return (
    <div className="doc-canvas-wrap">
      {loading && (
        <div className="doc-cover-loading">
          <Spinner animation="border" size="sm" variant="primary" />
        </div>
      )}
      <canvas ref={canvasRef} className="doc-canvas" style={{ opacity: loading ? 0 : 1 }} />
    </div>
  );
}

// ── Tarjeta de documento ──────────────────────────────────────────────────────
function DocCard({ doc, onVerMas }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`libro-card ${hovered ? 'libro-card--hovered' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onVerMas(doc)}
      role="button" tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onVerMas(doc)}
    >
      {/* Lomo del libro */}
      <div className="libro-lomo">
        <span className="libro-lomo-text">
          {doc.category || new Date(doc.publish_date).getFullYear() || ''}
        </span>
      </div>

      {/* Portada */}
      <div className="libro-portada">
        <PdfCover url={doc.file_url} title={doc.title} />
        <div className="libro-overlay">
          <div className="libro-overlay-content">
            <div className="libro-overlay-icon">👁</div>
            <div className="libro-overlay-text">Ver documento</div>
          </div>
        </div>
      </div>

      {/* Sombra */}
      <div className="libro-sombra" />
    </div>
  );
}

// ── Modal de detalle ──────────────────────────────────────────────────────────
function DocModal({ doc, onHide }) {
  if (!doc) return null;
  return (
    <Modal show={!!doc} onHide={onHide} centered size="md">
      <Modal.Header closeButton style={{ borderBottom: "3px solid #1a3a6b" }}>
        <Modal.Title style={{ fontSize: 16, fontWeight: 800, color: "#1a3a6b" }}>
          {doc.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
          <div style={{ width: 120, flexShrink: 0, borderRadius: 8, overflow: "hidden", border: "1px solid #dde3ef" }}>
            <PdfCover url={doc.file_url} title={doc.title} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 12 }}>
              <Badge bg="primary" style={{ marginRight: 8 }}>PDF</Badge>
              {doc.category && <Badge bg="secondary">{doc.category}</Badge>}
            </div>
            {doc.publish_date && (
              <div style={{ fontSize: 13, color: "#7a8aaa", marginBottom: 8 }}>
                <strong>Fecha:</strong> {new Date(doc.publish_date).toLocaleDateString("es-HN")}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
              <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                className="btn btn-primary btn-sm" style={{ textAlign: "center" }}>
                👁 Ver documento
              </a>
              <a href={doc.file_url} download
                className="btn btn-outline-primary btn-sm" style={{ textAlign: "center" }}>
                ⬇ Descargar PDF
              </a>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
const Biblioteca = () => {
  const { settings } = useSiteSettings();
  const bannerSrc = settings.banner_image
    ? `https://facach.coop.hn${settings.banner_image}`
    : fallbackBanner;

  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  useEffect(() => {
    fetch(`${API_URL}/library/categories`)
      .then(r => r.json())
      .then(json => { if (json.ok) setCategories(json.data); })
      .catch(err => console.error("Error cargando categorías:", err.message));
  }, []);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ q: query, category_id: categoryId, page, limit: pageSize });
      const res = await fetch(`${API_URL}/library?${params}`);
      const json = await res.json();
      if (json.ok) {
        setDocs(json.data);
        setTotal(json.total);
        setTotalPages(json.totalPages || 1);
      }
    } catch (err) {
      console.error("Error cargando biblioteca:", err.message);
    } finally { setLoading(false); }
  }, [query, categoryId, page, pageSize]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);
  useEffect(() => { setPage(1); }, [query, categoryId, pageSize]);

  return (
    <>
      <NavbarFacach />

      {/* Banner */}
      <div className="banner-wrapper">
        <Image src={bannerSrc} alt="Biblioteca Digital - FACACH" fluid className="banner-image" />
        <div className="banner-overlay">
          <h1 className="banner-title">FACACH | BIBLIOTECA DIGITAL</h1>
        </div>
      </div>

      <div
        className="watermarked"
        style={{ ["--wm-image"]: `url(${watermark})`, ["--wm-opacity"]: 0.1 }}
      />

      <Container className="py-4">

        {/* Filtros — mismo estilo que Memorias */}
        <section className="filtros-section" aria-label="Filtros de búsqueda">
          <Row className="g-3 align-items-end mb-2">
            <Col xs={12} md={5}>
              <Form.Group>
                <Form.Label className="filtro-label">Buscar por título</Form.Label>
                <Form.Control
                  type="search"
                  placeholder="Buscar por título…"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="filtro-label">Categoría</Form.Label>
                <Form.Select value={categoryId} onChange={e => setCategoryId(e.target.value)}>
                  <option value="">Todas las categorías</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label className="filtro-label">Por página</Form.Label>
                <Form.Select value={pageSize} onChange={e => setPageSize(parseInt(e.target.value, 10))}>
                  <option value={8}>8</option>
                  <option value={12}>12</option>
                  <option value={16}>16</option>
                  <option value={24}>24</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Resumen */}
          <div className="d-flex justify-content-between align-items-center mb-3 small text-muted">
            <span>
              {loading ? "Cargando…" : total === 0
                ? "0 resultados"
                : `${total} resultado${total > 1 ? "s" : ""}`}
            </span>
            <span>Página {page} de {totalPages}</span>
          </div>
        </section>

        {/* Grid de documentos */}
        <section className="documentos-section">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : docs.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <div style={{ fontSize: 40, marginBottom: 12 }}>📚</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1a3a6b" }}>
                {query ? `Sin resultados para "${query}"` : "No hay documentos publicados aún."}
              </div>
            </div>
          ) : (
            <div className="docs-grid">
              {docs.map(doc => (
                <div key={doc.id} className="docs-grid-item">
                  <DocCard doc={doc} onVerMas={setSelected} />
                  <div className="libro-info">
                    <div className="libro-titulo">{doc.title}</div>
                    {doc.category && <div className="libro-anio">{doc.category}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4">
            <Pagination className="mb-0">
              <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
              <Pagination.Prev onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} />

              {page > 2 && (
                <>
                  <Pagination.Item onClick={() => setPage(1)}>1</Pagination.Item>
                  {page > 3 && <Pagination.Ellipsis disabled />}
                </>
              )}

              {pageWindow(page, totalPages, 1).map(p => (
                <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>
                  {p}
                </Pagination.Item>
              ))}

              {page < totalPages - 1 && (
                <>
                  {page < totalPages - 2 && <Pagination.Ellipsis disabled />}
                  <Pagination.Item onClick={() => setPage(totalPages)}>{totalPages}</Pagination.Item>
                </>
              )}

              <Pagination.Next onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} />
              <Pagination.Last onClick={() => setPage(totalPages)} disabled={page === totalPages} />
            </Pagination>
          </div>
        )}
      </Container>

      <FooterFacach />
      <DocModal doc={selected} onHide={() => setSelected(null)} />
    </>
  );
};

export default Biblioteca;